/**
 * <eveline-gift-selector>
 *
 * mode="atc"      — cart drawer: heading + carousel + progress bar with checkpoints
 * mode="progress" — PDP / any page: simple progress bar only
 *
 * Product markup and tier thresholds are rendered server-side by Liquid.
 * JS handles: carousel navigation, cart-state-based show/unlock, progress bar.
 */

if (!customElements.get("eveline-gift-selector")) {

/** Maps Shopify market handle → BCP 47 locale for Intl.NumberFormat */
const MARKET_TO_LOCALE = { pl: "pl", de: "de", cz: "cs", ua: "uk", hu: "hu" };

class EvelineGiftSelector extends HTMLElement {
  static get observedAttributes() {
    return ["market"];
  }

  connectedCallback() {
    this._cart = null;
    this._curCard = 0;
    this._initialized = false;
    this._listeners = [];
    this._init();
  }

  disconnectedCallback() {
    for (const [el, type, fn] of this._listeners)
      el.removeEventListener(type, fn);
  }

  attributeChangedCallback() {
    if (this._initialized && this._cart !== null) this._render();
  }

  _init() {
    if (this.closest(".drawer--root")) this.dataset.view = "drawer";
    this.mode = this.getAttribute("mode") || "atc";
    this._currency = this.dataset.currency || "EUR";
    this._locale = MARKET_TO_LOCALE[this.getAttribute("market")] || "en";

    if (this.mode === "atc") this._initSwiper();
    this._attachATC();
    this._bindCartEvents();
    this._fetchCart();
    this._initialized = true;
  }

  _initSwiper() {
    this._swiper = null;
    this._pendingSlide = null;
    this._tryInitSwiper(30);
  }

  _tryInitSwiper(attempts) {
    if (typeof Swiper !== "undefined") {
      const el = this.querySelector("[data-swiper]");
      if (!el) return;
      const isPage = this.dataset.view ? this.dataset.view : false;
      const breakpointsMap = {
        "page-product": { 640: { slidesPerView: 1.1 }, 1300: { slidesPerView: 1.35 } },
        "page-cart":    { 640: { slidesPerView: 1.3 }, 768: { slidesPerView: 1.5 } },
        "drawer":       { 640: { slidesPerView: 1.1 } }
      };
      this._swiper = new Swiper(el, {
        slidesPerView: 1.1,
        grabCursor: true,
        spaceBetween: isPage ? 16 : 12,
        slidesOffsetBefore: 24,
        slidesOffsetAfter: 24,
        centeredSlides: !isPage,
        centeredSlidesBounds: !isPage,
        navigation: {
          prevEl: this.querySelector("[data-prev]"),
          nextEl: this.querySelector("[data-next]"),
        },
        breakpoints: breakpointsMap[isPage] ?? { 768: { slidesPerView: 1.1 } },
      });
      // Prevent Blockshop's SwipeListener from closing the drawer when swiping
      // the carousel. SwipeListener dispatches "swipe" on document synchronously
      // inside its own touchend handler. We block it by registering a capture-phase
      // listener that calls stopImmediatePropagation() before Blockshop's handler.
      // Cleanup is registered on document (not el) so it always fires, and after
      // SwipeListener's touchend runs (SwipeListener was registered first at page load).
      el.addEventListener("touchstart", () => {
        const blockSwipe = (ev) => ev.stopImmediatePropagation();
        document.addEventListener("swipe", blockSwipe, { capture: true });
        const cleanup = () => {
          document.removeEventListener("swipe", blockSwipe, { capture: true });
        };
        document.addEventListener("touchend", cleanup, { once: true, passive: true });
        document.addEventListener("touchcancel", cleanup, { once: true, passive: true });
      }, { passive: true });
      if (this._pendingSlide !== null) {
        this._swiper.slideTo(this._pendingSlide, 0);
        this._pendingSlide = null;
      }
    } else if (attempts > 0) {
      setTimeout(() => this._tryInitSwiper(attempts - 1), 100);
    }
  }

  _attachATC() {
    this.querySelectorAll("[data-add-gift]").forEach((btn) => {
      btn.addEventListener("click", () =>
        this._addGiftToCart(btn.dataset.variantId, btn.dataset.tierId, btn),
      );
    });
  }

  _cards() {
    return [...this.querySelectorAll(".evgs__card")];
  }


  _bindCartEvents() {
    let rafId = null;
    const refresh = () => {
      cancelAnimationFrame(rafId);
      rafId = requestAnimationFrame(() => this._fetchCart());
    };

    const onDoc = (type) => {
      document.addEventListener(type, refresh);
      this._listeners.push([document, type, refresh]);
    };
    const onWin = (type) => {
      window.addEventListener(type, refresh);
      this._listeners.push([window, type, refresh]);
    };

    // Document-level events (eveline custom)
    onDoc("eveline:cart:updated");

    // Blockshop theme events — fired on window via window.trigger() wrapper
    onWin("theme:cart:updated");
    onWin("theme:cart:productAdded");
  }

  async _fetchCart() {
    if (this._fetchAbort) this._fetchAbort.abort();
    this._fetchAbort = new AbortController();
    try {
      const res = await fetch("/cart.js", { signal: this._fetchAbort.signal });
      this._cart = await res.json();
    } catch (e) {
      if (e?.name === "AbortError") return;
      this._cart = null;
    }
    this._fetchAbort = null;
    this._render();
  }

  _cartItems() {
    return this._cart?.items ?? [];
  }
  _giftLines() {
    return this._cartItems().filter((i) => i.properties?.["_gift_tier_id"]);
  }
  _regularSubtotal() {
    const div =
      this._cart?.currency === "JPY" || this._cart?.currency === "KRW"
        ? 1
        : 100;
    return this._cartItems()
      .filter((i) => !i.properties?.["_gift_tier_id"])
      .reduce((s, i) => s + i.line_price / div, 0);
  }

  _render() {
    if (this.mode === "progress") {
      this._renderProgress();
    } else {
      this._renderSelector();
    }
  }

  _renderSelector() {
    const subtotal = this._regularSubtotal();
    const hasGift = this._giftLines().length > 0;
    const isPdp = this.dataset.view === "page-product";

    if (hasGift) {
      this._giftWasInCart = true;
      // On PDP keep the selector permanently visible — it's part of the product card.
      // In drawer / page-cart we still hide once a gift has been added.
      if (!isPdp) {
        this.classList.remove("is-visible");
        return;
      }
    } else if (this._giftWasInCart) {
      // Gift was just removed — fire global event for other components
      this._giftWasInCart = false;
      window.dispatchEvent(new CustomEvent("eveline:gift:removed", { bubbles: true }));
    }

    this.classList.add("is-visible");

    const cards = this._cards();
    if (!cards.length) return;

    const hideLocked = this.getAttribute("hide-locked") === "true";

    // Update card states
    cards.forEach((card) => {
      const threshold = Number(card.dataset.threshold);
      const unlocked = subtotal >= threshold;
      card.classList.toggle("is-active", unlocked);
      if (hideLocked) card.classList.toggle("is-hidden", !unlocked);

      const btn = card.querySelector("[data-add-gift]");
      if (btn) {
        // Hide add button when locked or when a gift is already in cart (one-gift rule)
        btn.classList.toggle("is-hidden", !unlocked || hasGift);
        btn.setAttribute("aria-busy", "false");
      }

      const lockedMsg = card.querySelector(".evgs__locked-msg");
      if (lockedMsg) {
        if (!unlocked) {
          const remaining = this._fmt(threshold - subtotal);
          const tpl = this.dataset.msgAddMore || "";
          lockedMsg.innerHTML = tpl.replace("{amount}", `<strong>${this._esc(remaining)}</strong>`);
          lockedMsg.classList.remove("is-hidden");
        } else {
          lockedMsg.classList.add("is-hidden");
        }
      }
    });

    if (hideLocked && this._swiper) this._swiper.update();

    // On initial render, jump to the highest (last) unlocked tier
    let lastUnlockedIdx = -1;
    cards.forEach((card, i) => {
      if (subtotal >= Number(card.dataset.threshold)) lastUnlockedIdx = i;
    });
    if (lastUnlockedIdx > this._curCard) {
      this._curCard = lastUnlockedIdx;
      if (this._swiper) {
        this._swiper.slideTo(this._curCard, 0);
      } else {
        this._pendingSlide = this._curCard;
      }
    }

    // Progress bar with checkpoints
    this._renderCheckpointProgress(subtotal, cards);
  }

  _renderCheckpointProgress(subtotal, cards) {
    const wrap = this.querySelector("[data-progress]");
    if (!wrap) return;

    const tiers = cards.map((c) => ({
      threshold: Number(c.dataset.threshold),
      productTitle: c.dataset.productTitle || "",
      origPrice: c.dataset.origPrice || "",
      giftPrice: c.dataset.giftPrice || "",
    }));

    if (!tiers.length) return;

    const maxThreshold = tiers[tiers.length - 1].threshold;
    const pct = Math.min((subtotal / maxThreshold) * 100, 100);
    const nextTier = tiers.find((t) => subtotal < t.threshold);

    // Progress text
    let text = "";
    if (!nextTier) {
      text = `<span>${this._esc(this.dataset.msgAllUnlocked || "")}</span>`;
    } else {
      const remaining = this._fmt(nextTier.threshold - subtotal);
      const tpl =
        this.dataset.msgAddMoreProduct || this.dataset.msgAddMore || "";
      text = tpl
        .replace("{amount}", `<strong>${this._esc(remaining)}</strong>`)
        .replace("{product_name}", this._esc(nextTier.productTitle))
        .replace("{product_old_price}", nextTier.origPrice ? `${this._esc(nextTier.origPrice)}` : "")
        .replace("{product_new_price}", nextTier.giftPrice ? `<strong>${this._esc(nextTier.giftPrice)}</strong>` : "");
    }

    // Dots
    const dots = tiers
      .map((t) => {
        const dotPct = (t.threshold / maxThreshold) * 100;
        const reached = subtotal >= t.threshold;
        return `
        <span class="evgs__dot${reached ? " is-reached" : ""}"
              style="left:${dotPct}%">
          <span class="evgs__dot-label">${this._esc(this._fmt(t.threshold))}</span>
        </span>`;
      })
      .join("");

    wrap.innerHTML = `
      <p class="evgs__progress-text">${text}</p>
      <div class="evgs__bar-wrap">
        <div class="evgs__bar-track">
          <div class="evgs__bar-fill" style="width:${pct}%"></div>
          ${dots}
        </div>
      </div>`;
  }

  _renderProgress() {
    const progress = this.querySelector("[data-progress]");
    if (!progress) return;

    const tiers = [...this.querySelectorAll("[data-tier-id][data-threshold]")]
      .map((el) => ({
        id: el.dataset.tierId,
        threshold: Number(el.dataset.threshold),
      }))
      .sort((a, b) => a.threshold - b.threshold);

    if (!tiers.length) return;

    const subtotal = this._regularSubtotal();
    const nextTier = tiers.find((t) => subtotal < t.threshold);

    if (!nextTier) {
      progress.innerHTML = `
        <div class="evgs__simple-progress">
          <p class="evgs__simple-text">${this._esc(this.dataset.msgAllUnlocked || "")}</p>
        </div>`;
      return;
    }

    const idx = tiers.findIndex((t) => t.id === nextTier.id);
    const prevThreshold = idx > 0 ? tiers[idx - 1].threshold : 0;
    const range = nextTier.threshold - prevThreshold;
    const pct =
      range > 0
        ? Math.max(0, Math.min(100, ((subtotal - prevThreshold) / range) * 100))
        : 0;
    const remaining = this._fmt(nextTier.threshold - subtotal);
    const msg = (this.dataset.msgAddMore || "").replace("{amount}", remaining);

    progress.innerHTML = `
      <div class="evgs__simple-progress">
        <p class="evgs__simple-text">${msg}</p>
        <div class="evgs__simple-bar"
             role="progressbar"
             aria-valuenow="${Math.round(pct)}"
             aria-valuemin="0"
             aria-valuemax="100">
          <div class="evgs__simple-fill" style="width:${pct}%"></div>
        </div>
      </div>`;
  }

  async _addGiftToCart(variantGid, tierId, btn) {
    const numericId = parseInt(variantGid.split("/").pop(), 10);
    btn.setAttribute("aria-busy", "true");

    try {
      const res = await fetch("/cart/add.js", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          items: [
            {
              id: numericId,
              quantity: 1,
              properties: { _gift_tier_id: tierId },
            },
          ],
          sections: ["cart-drawer"],
        }),
      });

      if (res.ok) {
        const data = await res.json();
        const sections = data.sections || {};

        // Update GWP component state first
        await this._fetchCart();

        if (typeof window.trigger === "function" && sections["cart-drawer"]) {
          // Mirror Cart.openDrawer: open drawer once theme:cart:updated fires
          window.addEventListener(
            "theme:cart:updated",
            () => {
              requestAnimationFrame(() => {
                window.trigger("theme:transition:reset:cart-drawer");
                window.trigger("theme:drawer:open", {
                  side: "right",
                  view: "cart-drawer",
                  trigger: null,
                });
              });
            },
            { once: true },
          );

          // Mirror Cart.updateAllHtml: push fresh section HTML into the drawer
          window.trigger("theme:cart:drawer:updateHtml", {
            section: sections["cart-drawer"],
            load_transitions: true,
          });
        } else {
          // Fallback if window.trigger not available or no section HTML returned
          document.dispatchEvent(
            new CustomEvent("eveline:cart:updated", { bubbles: true }),
          );
          this._openCartDrawer();
        }
      } else {
        const err = await res.json();
        console.error("[eveline-gift] cart/add.js failed:", res.status, err);
        btn.setAttribute("aria-busy", "false");
        this.dispatchEvent(
          new CustomEvent("eveline:add-gift:error", {
            detail: err,
            bubbles: true,
          }),
        );
      }
    } catch {
      btn.disabled = false;
      btn.setAttribute("aria-busy", "false");
    }
  }

  _openCartDrawer() {
    const detail = { side: "right", view: "cart-drawer", trigger: null };
    if (typeof window.trigger === "function") {
      window.trigger("theme:drawer:open", detail);
    } else {
      window.dispatchEvent(
        new CustomEvent("theme:drawer:open", { detail }),
      );
    }
  }

  _fmt(amount) {
    try {
      return new Intl.NumberFormat(this._locale, {
        style: "currency",
        currency: this._currency,
        minimumFractionDigits: 0,
        maximumFractionDigits: 2,
      }).format(amount);
    } catch {
      return `${amount}\u00a0${this._currency}`;
    }
  }

  _esc(str) {
    return String(str)
      .replace(/&/g, "&amp;")
      .replace(/</g, "&lt;")
      .replace(/>/g, "&gt;")
      .replace(/"/g, "&quot;")
      .replace(/'/g, "&#39;");
  }
}

  customElements.define("eveline-gift-selector", EvelineGiftSelector);
}
